import { Controller } from "@hotwired/stimulus"

// Live updates for the public FBA calculator. The server does all the maths:
// on input we debounce, submit the GET form into the results turbo-frame, and
// mirror the form into the address bar so the page can be shared. Without
// JavaScript the Calculate button submits the form normally.
export default class extends Controller {
  static targets = ["copy", "status", "category", "more"]
  static values = { delay: { type: Number, default: 250 } }

  // With JavaScript the form updates live, so the Calculate button is redundant.
  connect() {
    this.element.classList.add("fbc-live")
    // A live update that leaves a fee with no row (US calculator) opens the section where it can be typed.
    this.onFrameLoad = (event) => {
      if (event.target.id !== "calc-results" || !this.hasMoreTarget) return
      if (event.target.querySelector("[data-missing-fee]")) this.moreTarget.open = true
    }
    document.addEventListener("turbo:frame-load", this.onFrameLoad)
    this.wasOther = this.hasCategoryTarget && this.categoryTarget.value === (this.categoryTarget.dataset.otherValue || "Other")
    if (this.hasCopyTarget) this.copyTarget.hidden = false
  }

  // Each category option ends with the referral rate at the current selling price. The tiers come
  // from the server (data-tiers); this only picks the tier for the typed price.
  updateCategoryRates() {
    if (!this.hasCategoryTarget) return
    const price = parseFloat(this.element.querySelector("#calc_price")?.value)
    if (!(price > 0)) return

    for (const option of this.categoryTarget.options) {
      if (!option.dataset.tiers) continue
      const tiers = JSON.parse(option.dataset.tiers)
      const tier = tiers.find(([upto]) => upto === null || price <= upto)
      if (tier) option.textContent = `${option.dataset.label} (${Number(tier[1])}%)`
    }
  }

  // Choosing the "Other" category means there is no rate card to read, so the fee boxes it needs become
  // required: open their section and take the user to the first one. The select names its Other value
  // (data-other-value), the boxes that become required (data-required-fields) and the boxes to clear when
  // leaving it (data-clear-fields, defaulting to the required ones). Values are comma separated element ids.
  syncCategoryMode() {
    if (!this.hasCategoryTarget) return
    const select = this.categoryTarget
    const otherValue = select.dataset.otherValue || "Other"
    const find = (list) => list.split(",").map((id) => this.element.querySelector(`#${id}`)).filter(Boolean)
    const fields = find(select.dataset.requiredFields || "calc_referral_pct")
    const clearing = find(select.dataset.clearFields || select.dataset.requiredFields || "calc_referral_pct")
    const other = select.value === otherValue
    fields.forEach((field) => { field.placeholder = other ? "Required" : "Auto" })
    if (other && !this.wasOther && this.hasMoreTarget) {
      this.moreTarget.open = true
      const first = fields[0]
      first?.scrollIntoView({ block: "center", behavior: "smooth" })
      first?.focus({ preventScroll: true })
    }
    // Leaving Other: the fees typed for it would silently override the new category's rate card.
    if (!other && this.wasOther) clearing.forEach((field) => { field.value = "" })
    this.wasOther = other
  }

  queue() {
    this.syncCategoryMode()
    this.updateCategoryRates()
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.submit(), this.delayValue)
  }

  submit() {
    this.syncUrl()
    this.element.requestSubmit()
  }

  // Blank fields are left out so the address stays short.
  syncUrl() {
    const params = new URLSearchParams()
    for (const [key, value] of new FormData(this.element)) {
      if (value !== "") params.append(key, value)
    }
    const query = params.toString()
    history.replaceState(history.state, "", `${location.pathname}${query ? `?${query}` : ""}`)
  }

  // Copies the address of the current scenario (inputs are mirrored into the URL).
  // navigator.clipboard needs a secure context, so plain-http pages fall back to execCommand.
  async copyLink() {
    this.syncUrl()
    const url = location.href
    if (await this.writeToClipboard(url)) {
      this.announce("Link copied")
    } else {
      window.prompt("Copy this link", url)
    }
  }

  async writeToClipboard(text) {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
        return true
      }
    } catch {
      // fall through to the legacy path
    }
    const field = document.createElement("textarea")
    field.value = text
    field.setAttribute("readonly", "")
    field.style.cssText = "position:fixed;top:0;left:0;opacity:0"
    document.body.appendChild(field)
    field.select()
    try {
      return document.execCommand("copy")
    } catch {
      return false
    } finally {
      field.remove()
    }
  }

  announce(message) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message
    clearTimeout(this.statusTimer)
    this.statusTimer = setTimeout(() => { this.statusTarget.textContent = "" }, 2500)
  }

  disconnect() {
    document.removeEventListener("turbo:frame-load", this.onFrameLoad)
    clearTimeout(this.statusTimer)
    clearTimeout(this.timer)
  }
}
